/* eslint-disable max-lines */
import { LeftOutlined } from '@ant-design/icons';
import { Button, Layout, Menu, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import type { ItemType } from 'antd/es/menu/hooks/useItems';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { DashboardHeaderConstants } from '../../../../pages/Dashboard/DashboardHeader/DashboardHeaderConstants';
import UserAvatar from '../../../ui/UserAvatar/UserAvatar';
import { getEnv, openInNewTab } from '../../../../libs/helper/HelperFunctions';
import {
  AssessJourneySection,
  IAuditHistoryPayload,
  MigrateModernizeTabs,
  SiteReliabilitySection,
} from '../../../../libs/models/DashboardModels';
import { storage, STORAGE_CONSTANTS } from '../../../../libs/services/LocalStorage';
import { BuildDeployActions } from '../../../../libs/store/actions/BuildDeployActions';
import { DashboardActions } from '../../../../libs/store/actions/DashboardActions';
import { LoginActions } from '../../../../libs/store/actions/LoginActions';
import { SidebarActions } from '../../../../libs/store/actions/SidebarActions';
import { IApplicationState, useAppSelector } from '../../../../libs/store/reducers';
import { IUserPermission } from '../../../../libs/models/UserModels';
import { routes } from '../../../../navigation/Routes';
import { AiEngineTabKeys } from '../../../../pages/Dashboard/AiEngine/AiEngineConstants';
import {
  ApplicationUserTypes,
  ISidebarItem,
  menuSections,
  MENU_ITEMS,
  SideBarKey,
  SidebarLabels,
} from './SidebarConstants';
import SidebarOptionsModal from './SidebarOptionsModal';
import './SidebarStyles.less';

const { Sider } = Layout;

const AiSparkleStar: React.FC<{ className: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M12 2 L13.8 9.2 L21 11 L13.8 12.8 L12 20 L10.2 12.8 L3 11 L10.2 9.2 Z"
      fill="none"
      stroke="#6AAF1F"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

const DcaAiLogo: React.FC<{ src: string; className: string; alt?: string }> = ({
  src,
  className,
  alt = 'DCA Logo',
}) => {
  const isCollapsed = className.includes('tangra-logo') && !className.includes('expanded');
  return (
    <span className={`dca-ai-logo-wrap ${isCollapsed ? 'dca-ai-logo-wrap--collapsed' : ''}`}>
      <img className={className} src={src} alt={alt} />
      <AiSparkleStar className="dca-ai-star dca-ai-star-big" />
      <AiSparkleStar className="dca-ai-star dca-ai-star-up" />
      <AiSparkleStar className="dca-ai-star dca-ai-star-down" />
    </span>
  );
};

const Sidebar = () => {
  const email = storage.getItem(STORAGE_CONSTANTS.email);
  const [isAdminPopoverOpen, setIsAdminPopoverOpen] = useState<boolean>(false);
  const [isMigrate, setIsMigrate] = useState<boolean>(false);
  const [flyout, setFlyout] = useState<{
    parentKey: string;
    top: number;
    left: number;
  } | null>(null);
  const flyoutCloseTimer = useRef<number | null>(null);

  const dispatch = useDispatch();
  const history = useHistory();
  const {
    isExpanded,
    hiddenSidebar,
    activeKey,
    project,
    account,
    logoutState,
    enabledDiscoverAssessItems,
    userDetails,
  } = useAppSelector(
    ({
      sidebar: { isExpanded, hiddenSidebar, activeKey, logout },
      overview: {
        selectedAccount,
        selectedProject,
        selectedAccountId,
        selectedAssetType,
        selectedProjectId,
      },
      dashboard: { enabledDiscoverAssessItems },
      user: { userDetails },
    }: IApplicationState) => ({
      userDetails,
      isExpanded,
      hiddenSidebar,
      activeKey,
      project:
        selectedProject && selectedProjectId
          ? { name: selectedProject, id: selectedProjectId }
          : undefined,
      account:
        selectedAccount && selectedAccountId && selectedAssetType
          ? { name: selectedAccount, id: selectedAccountId, assetType: selectedAssetType }
          : undefined,
      logoutState: logout,
      enabledDiscoverAssessItems,
    }),
  );

  const dispatcher = {
    toggleSidebar: (data: boolean) => dispatch(SidebarActions.toggleSidebar(data)),
    toggleHiddenSidebar: (data: boolean) => dispatch(SidebarActions.toggleHiddenSidebar(data)),
    setActiveKey: (data: string) => dispatch(SidebarActions.setSidebarKey(data)),
    saveAuditDetails: (data: IAuditHistoryPayload) =>
      dispatch(DashboardActions.saveAuditHistoryStart(data)),
    toggleLogout: (data: boolean) => dispatch(SidebarActions.toggleLogout(data)),
    postSessionToken: () => dispatch(LoginActions.sessionTokenPostStart()),
    setActiveDashboardTitleState: (activeTitleName: string) =>
      dispatch(DashboardActions.setActiveDashboardTitleState(activeTitleName)),
  };

  const setActiveKey = (data: string) => dispatcher.setActiveKey(data);
  const toggleSidebar = () => dispatcher.toggleSidebar(!isExpanded);
  const toggleHiddenSidebar = (data: boolean) => dispatcher.toggleHiddenSidebar(data);
  const saveAuditDetails = (data: IAuditHistoryPayload) => dispatcher.saveAuditDetails(data);
  const toggleLogout = (data: boolean) => dispatcher.toggleLogout(data);
  const postSessionToken = () => dispatcher.postSessionToken();
  const setActiveDashboardTitleState = (activeTitleName: string) =>
    dispatcher.setActiveDashboardTitleState(activeTitleName);
  const allowedMenuItems = MENU_ITEMS.filter(({ permission }) =>
    permission
      ? userDetails?.user_permissions &&
        userDetails?.user_permissions?.[permission as keyof IUserPermission]
      : true,
  );

  const applySubTabSelection = (childKey: string) => {
    switch (childKey) {
      // Cloud Strategy
      case 'cloud-strategy-discover':
        dispatch(DashboardActions.setSelectedAssessTab(AssessJourneySection.PRE_CLOUD));
        return;
      case 'cloud-strategy-assess':
        dispatch(DashboardActions.setSelectedAssessTab(AssessJourneySection.ANALYSIS));
        return;
      case 'cloud-strategy-plan':
        dispatch(DashboardActions.setSelectedAssessTab(AssessJourneySection.POST_CLOUD));
        return;
      // Environment Build
      case 'env-build-architecture-design':
        dispatch(BuildDeployActions.setSelectedTab('Architecture & Design'));
        return;
      case 'env-build-foundations':
        dispatch(BuildDeployActions.setSelectedTab('Build Cloud Foundations'));
        return;
      case 'env-build-deployment-pipelines':
        dispatch(BuildDeployActions.setSelectedTab('Deployment Pipelines'));
        return;
      // Environment Runways
      case 'runways-environments':
        dispatch(
          DashboardActions.setSelectedMigrateModernizeTab({
            tabname: MigrateModernizeTabs.ENVIRONMENTS,
            isModalOpen: false,
          }),
        );
        return;
      case 'runways-workload-patterns':
        dispatch(
          DashboardActions.setSelectedMigrateModernizeTab({
            tabname: MigrateModernizeTabs.WORKLOAD_PATTERNS,
            isModalOpen: false,
          }),
        );
        return;
      case 'runways-apps-solutions':
        dispatch(
          DashboardActions.setSelectedMigrateModernizeTab({
            tabname: MigrateModernizeTabs.APPS_AND_SOLUTIONS,
            isModalOpen: false,
          }),
        );
        return;
      case 'runways-platforms':
        dispatch(
          DashboardActions.setSelectedMigrateModernizeTab({
            tabname: MigrateModernizeTabs.PLATFORMS,
            isModalOpen: false,
          }),
        );
        return;
      // Site Reliability Engineering
      case 'sre-observability':
        dispatch(
          DashboardActions.setSelectedSiteReliabilityTab(SiteReliabilitySection.OBSERVABILITY),
        );
        return;
      case 'sre-chaos-engineering':
        dispatch(
          DashboardActions.setSelectedSiteReliabilityTab(
            SiteReliabilitySection.CHAOS_ENGINEERING,
          ),
        );
        return;
      case 'sre-reliability-metrics':
        dispatch(
          DashboardActions.setSelectedSiteReliabilityTab(SiteReliabilitySection.SITE_RELIABILITY),
        );
        return;
      case 'sre-ai-tokenomics':
        dispatch(
          DashboardActions.setSelectedSiteReliabilityTab(SiteReliabilitySection.AI_TOKENOMICS),
        );
        return;
      default:
        return;
    }
  };

  const getSubTabRouteState = (childKey: string): { aiEngineTab?: string } | undefined => {
    const aiTabMap: Record<string, string> = {
      'ai-agent-registry': AiEngineTabKeys.AGENTS,
      'ai-skills-registry': AiEngineTabKeys.SKILLS,
      'ai-toolkit-registry': AiEngineTabKeys.TOOLKITS,
      'ai-model-registry': AiEngineTabKeys.MODELS,
      'ai-llm-health': AiEngineTabKeys.LLM_HEALTH,
      'ai-llm-settings': AiEngineTabKeys.LLM_SETTINGS,
      'ai-tai-guardrails': AiEngineTabKeys.TAI_GUARDRAILS,
    };
    return aiTabMap[childKey] ? { aiEngineTab: aiTabMap[childKey] } : undefined;
  };

  const onClickOperations = (menuItem: ISidebarItem) => {
    if (menuItem.parent) {
      applySubTabSelection(menuItem.key);
    }
    history.push({
      pathname: menuItem.path,
      state: getSubTabRouteState(menuItem.key),
    });
    dispatcher.setActiveKey(menuItem.key);
    menuItem.activeTabTitle && setActiveDashboardTitleState(menuItem.activeTabTitle);
  };

  const cancelFlyoutClose = () => {
    if (flyoutCloseTimer.current) {
      window.clearTimeout(flyoutCloseTimer.current);
      flyoutCloseTimer.current = null;
    }
  };

  const openFlyoutForItem = (parent: ISidebarItem, anchor: HTMLElement) => {
    cancelFlyoutClose();
    const rect = anchor.getBoundingClientRect();
    const viewportH = window.innerHeight;
    // Estimate flyout height: 1 title row + N items + paddings (~40 + 36*items + 16).
    const childCount = allowedMenuItems.filter(
      (mi) => !mi.disable && mi.parent === parent.key,
    ).length;
    const estHeight = Math.min(40 + 36 * Math.max(childCount, 1) + 16, viewportH - 16);
    const margin = 8;
    // Anchor bottom of flyout to bottom of the menu item so the panel rises upward.
    let top = rect.bottom - estHeight;
    if (top + estHeight > viewportH - margin) {
      top = viewportH - estHeight - margin;
    }
    if (top < margin) top = margin;
    setFlyout((current) =>
      current && current.parentKey === parent.key
        ? null
        : { parentKey: parent.key, top, left: rect.right },
    );
  };

  useEffect(() => {
    if (!flyout) return undefined;
    const handleDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.sidebar-flyout')) return;
      if (target.closest('.sidebar-items-container')) return;
      setFlyout(null);
    };
    document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, [flyout]);

  useEffect(() => {
    return () => cancelFlyoutClose();
  }, []);

  const flyoutParent = flyout
    ? allowedMenuItems.find((mi) => mi.key === flyout.parentKey)
    : undefined;
  const flyoutChildren = flyoutParent
    ? allowedMenuItems.filter(
        (mi) => !mi.disable && mi.parent && mi.parent === flyoutParent.key,
      )
    : [];

  const onClickFlyoutItem = (item: ISidebarItem) => {
    onClickOperations(item);
    setFlyout(null);
  };

  const onClickFlyoutHeader = () => {
    if (flyoutParent) {
      onClickOperations(flyoutParent);
      setFlyout(null);
    }
  };
  const logout = () => {
    postSessionToken();
    saveAuditDetails({
      dashboard: { project, account, enabledDiscoverAssessItems },
    });
  };

  if (logoutState) {
    toggleLogout(false);
    storage.clearAll();
    window.location.reload();
  }

  useEffect(() => {
    if (window.location.href.indexOf('cbo') > -1) {
      setIsMigrate(true);
    } else {
      setIsMigrate(false);
    }
  }, [activeKey]);

  let iconSrc = '';
  let altText = '';

  const getSubMenuLabels = (parentMenuItem: ISidebarItem): string[] => {
    return allowedMenuItems
      .filter((subMenuItem) => {
        return (
          !subMenuItem.disable && subMenuItem.parent && subMenuItem.parent === parentMenuItem.key
        );
      })
      .map((subMenuItem) => subMenuItem.label);
  };

  const menuElement = (menuItem: ISidebarItem, iconSrc: string, altText: string): ItemType => {
    const isAiEngine = menuItem.key === SideBarKey.AI_ENGINE;
    const menuContent = isAiEngine ? (
      <span className="ai-engine-sidebar-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 2.2l2.1 6.2 6.2 2.1-6.2 2.1L12 18.8 9.9 12.6 3.7 10.5l6.2-2.1L12 2.2z"
            fill="currentColor"
          />
          <path
            d="M19 14.2l.95 2.75L22.7 17.9l-2.75.95L19 21.6l-.95-2.75L15.3 17.9l2.75-.95L19 14.2z"
            fill="currentColor"
            opacity="0.75"
          />
          <path
            d="M5 15l.65 1.9 1.9.65-1.9.65L5 20.1l-.65-1.9L2.45 17.55l1.9-.65L5 15z"
            fill="currentColor"
            opacity="0.6"
          />
        </svg>
      </span>
    ) : (
      <img
        src={`${process.env.PUBLIC_URL}${iconSrc}`}
        alt={altText}
        className="dashboard-image"
      />
    );

    const baseClass = menuItem.parent ? 'cloud-journey-submenuitem' : 'menuitem-sidebar-style';
    const className = isAiEngine ? `${baseClass} ai-engine-menu-item` : baseClass;

    return {
      key: menuItem.key,
      icon: menuContent,
      label: menuItem.label,
      className,
      onClick: (info) => {
        if (menuItem.hasChildren) {
          const domEvent = info.domEvent as React.SyntheticEvent<HTMLElement>;
          const anchor =
            (domEvent?.currentTarget as HTMLElement) ||
            (domEvent?.target as HTMLElement);
          openFlyoutForItem(menuItem, anchor);
          return;
        }
        setFlyout(null);
        onClickOperations(menuItem);
      },
    };
  };

  const getSubMenu = (parentMenuItem: ISidebarItem) => {
    return allowedMenuItems
      .filter((subMenuItem) => {
        return (
          !subMenuItem.disable && subMenuItem.parent && subMenuItem.parent === parentMenuItem.key
        );
      })
      .map((subMenuItem) => {
        const subMenuAltText = `${subMenuItem.label} Icon`;
        if (activeKey === subMenuItem.key && subMenuItem.parent === parentMenuItem.key) {
          iconSrc = parentMenuItem.activeIcon;
        }
        const subMenuIconSrc =
          activeKey === subMenuItem.key ? subMenuItem.activeIcon : subMenuItem.icon;
        // To be implemented later for multi level menu
        // if(subMenuItem.hasChildren){
        //   return parentMenuElement(subMenuItem, subMenuIconSrc, subMenuAltText)
        // } else {
        //   return menuElement(subMenuItem, subMenuIconSrc, subMenuAltText);
        // }
        return menuElement(subMenuItem, subMenuIconSrc, subMenuAltText);
      });
  };

  const parentMenuElement = (subMenuItem: ISidebarItem, iconSrc: string, altText: string): ItemType => {
    const childMenu = getSubMenu(subMenuItem);
    return {
      key: subMenuItem.key,
      icon: (
        <img
          src={`${process.env.PUBLIC_URL}${iconSrc}`}
          alt={altText}
          className="dashboard-image"
        />
      ),
      label: subMenuItem.label,
      className: "sidebar-submenu-container",
      children: [
        {
          key: subMenuItem.label,
          label: subMenuItem.label,
          className: "cloud-journey-menuitem",
        },
        ...childMenu,
      ],
    };
  };

  const getParentMenu = (
    applicationType: ApplicationUserTypes,
    section: menuSections,
    menuLevel = 1,
  ) => {
    return allowedMenuItems
      .filter((menuItem) => {
        return (
          !menuItem.disable &&
          menuItem.menuSection &&
          menuItem.menuSection === section &&
          menuItem.type === applicationType &&
          menuItem.level &&
          menuItem.level === menuLevel &&
          !(
            menuItem.key === SideBarKey.FEEDBACK_FORM_ADMIN &&
            !userDetails?.user_permissions.add_account
          ) &&
          !(menuItem.key === SideBarKey.FEEDBACK && userDetails?.user_permissions.add_account)
        );
      })
      .map((menuItem) => {
        altText = `${menuItem.label} Icon`;
        // Sidebar is permanently dark; always use the white default icon so the
        // selected item icon doesn't disappear against the dark background.
        iconSrc = menuItem.icon;

        // All items are simple menu items that navigate directly (no expanding submenus)
        return menuElement(menuItem, iconSrc, altText);
      });
  };

  const MigrateSidebar = (
    <div className="sidebar-content">
      <div
        onClick={() => {
          history.push(routes.dashboard);
          setActiveDashboardTitleState(DashboardHeaderConstants.cloudSummary);
          setActiveKey(SideBarKey.CLOUD_SUMMARY);
        }}
        className="app-title cursor-pointer"
      >
        {isExpanded ? (
          <DcaAiLogo
            className="tangra-logo-expanded"
            src={`${process.env.PUBLIC_URL}/dca_logo_white.png`}
          />
        ) : (
          <DcaAiLogo
            className="tangra-logo"
            src={`${process.env.PUBLIC_URL}/dca_short_logo_white.png`}
          />
        )}
      </div>
      <div>
        {isExpanded ? (
          <>
            <Button type="default" className="dca-migrate" onClick={() => setIsMigrate(false)}>
              DCA
            </Button>
            <Button type="default" className="dca-migrate" onClick={() => setIsMigrate(true)}>
              Migration
            </Button>
          </>
        ) : (
          <div style={{ border: '1px solid #6E7164', borderRadius: '2px', padding: '0.5rem' }}>
            <img
              className="dca-migrate-switch"
              src={`${process.env.PUBLIC_URL}/dca.svg`}
              alt="DCA Logo"
              onClick={() => setIsMigrate(false)}
            />
            <img
              className="dca-migrate-switch"
              src={`${process.env.PUBLIC_URL}/migrate.svg`}
              alt="Migration Logo"
              onClick={() => setIsMigrate(true)}
            />
          </div>
        )}
      </div>
      <div className="sidebar-items-container">
        <Menu 
          defaultSelectedKeys={[activeKey]} 
          selectedKeys={[activeKey]} 
          mode="vertical"
          items={getParentMenu(ApplicationUserTypes.MIGRATE, menuSections.TOP)}
        />
      </div>
    </div>
  );
  // const dcaSwitch = (e :any) => {
  //   const env = getEnv();
  // }
  // const migrateSwitch = (url : string) => openInNewTab(url);
  const tangraSidebar = (
    <>
      <div className="sidebar-content">
        <div
          onClick={() => {
            history.push(routes.dashboard);
            setActiveDashboardTitleState(DashboardHeaderConstants.cloudSummary);
            setActiveKey(SideBarKey.CLOUD_SUMMARY);
          }}
          className="app-title cursor-pointer"
        >
          {isExpanded ? (
            <DcaAiLogo
              className="tangra-logo-expanded"
              src={`${process.env.PUBLIC_URL}/dca_logo_white.png`}
            />
          ) : (
            <DcaAiLogo
              className="tangra-logo"
              src={`${process.env.PUBLIC_URL}/dca_short_logo_white.png`}
            />
          )}
        </div>
        {/* <div> 
          {
            isExpanded ? (
              <>
              <Button
                type="default"
                className="dca-migrate"
                onClick={() => setIsMigrate(false)}
                >
                DCA
              </Button>
              <Button
                type="default"
                className="dca-migrate"
                onClick={ () => setIsMigrate(true)}
                >
                Migration
              </Button>
            </>) : (
            <div style={{ border : '1px solid #6E7164', borderRadius : '2px', padding : '0.5rem'}}>
              <img className="dca-migrate-switch" src={`${process.env.PUBLIC_URL}/dca.svg`} alt="DCA Logo"  onClick={() => setIsMigrate(false)}/>
              <img className="dca-migrate-switch" src={`${process.env.PUBLIC_URL}/migrate.svg`} alt="Migration Logo" onClick={ () => setIsMigrate(true)}/>
            </div>)
          }
          
        </div> */}
        <div
          className="sidebar-items-container"
        >
          <Menu 
            defaultSelectedKeys={[activeKey]} 
            selectedKeys={[activeKey]} 
            mode="vertical"
            items={getParentMenu(ApplicationUserTypes.TANGRA, menuSections.TOP)}
          />
          <div>
            <Menu 
              defaultSelectedKeys={[activeKey]} 
              selectedKeys={[activeKey]} 
              mode="vertical"
              items={getParentMenu(ApplicationUserTypes.TANGRA, menuSections.BOTTOM)}
            />
            {isExpanded && hiddenSidebar && (
              <LeftOutlined onClick={toggleSidebar} className="sidebar-trigger" />
            )}
            <div
              className="logout cursor-pointer"
              onClick={() => setIsAdminPopoverOpen(!isAdminPopoverOpen)}
            >
              <UserAvatar firstName={email} />
            </div>
          </div>
        </div>
      </div>
      {isAdminPopoverOpen && (
        <SidebarOptionsModal
          onLogout={logout}
          isAdminPopoverOpen={isAdminPopoverOpen}
          setIsAdminPopoverOpen={(value) => setIsAdminPopoverOpen(value)}
        />
      )}
    </>
  );

  return (
    <>
      <Sider
        breakpoint="sm"
        collapsible
        onBreakpoint={(broken) => toggleHiddenSidebar(broken)}
        collapsedWidth={hiddenSidebar ? 0 : 100}
        width={hiddenSidebar ? (isExpanded ? '100%' : 0) : 250}
        trigger={null}
        collapsed={!isExpanded}
        className={`sidebar-container sidebar-${isExpanded ? 'expanded' : 'closed'}`}
      >
        {tangraSidebar}
      </Sider>
      {flyout && flyoutParent && flyoutChildren.length > 0 && (
        <div
          className="sidebar-flyout"
          style={{ top: flyout.top, left: flyout.left }}
        >
          <div
            className="sidebar-flyout__title"
            onClick={onClickFlyoutHeader}
            role="button"
            tabIndex={0}
          >
            {flyoutParent.label}
          </div>
          <ul className="sidebar-flyout__list">
            {flyoutChildren.map((child) => (
              <li
                key={child.key}
                className={`sidebar-flyout__item${
                  activeKey === child.key ? ' is-active' : ''
                }`}
                onClick={() => onClickFlyoutItem(child)}
              >
                {child.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

export default Sidebar;
